import { host } from 'src/Branding/config';
import { useEffect, useState, useCallback } from 'react';
import { useThrottledValue } from './useThrottledValue';
import { DynamicOption } from '@refineintel/types';
import { errorHandler } from '@/Config/ErrorHandler';

interface PaginationState {
	totalCount: number;
	totalPages: number;
	currentPage: number;
	pageSize: number;
}

export const useQueryGetDynamicOptions = ({
	searchQuery,
	type,
	pagination,
	lang,
}: {
	searchQuery: string;
	type: string;
	pagination: { pageSize: number; currentPage: number };
	lang: string;
}) => {
	const [options, setOptions] = useState<DynamicOption[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [paginationInfo, setPaginationInfo] = useState<PaginationState>({
		totalCount: 0,
		totalPages: 0,
		currentPage: 1,
		pageSize: 50,
	});

	const throttledSearchQuery = useThrottledValue(searchQuery, 500);

	const fetchOptions = useCallback(
		async (controller: AbortController) => {
			setIsLoading(true);
			try {
				const url = new URL(`${host}/api/getDynamicOptions`);
				const params = new URLSearchParams({
					searchQuery: throttledSearchQuery,
					type,
					// lang,
					currentPage: pagination.currentPage.toString(),
					pageSize: pagination.pageSize.toString(),
				});
				url.search = params.toString();

				const res = await fetch(url, { signal: controller.signal });
				if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);

				const resData = await res.json();
				if (resData.status === 'error') throw new Error(resData.message);

				setOptions((prevOptions) =>
					pagination.currentPage === 1 ? resData.data : [...prevOptions, ...resData.data],
				);

				setPaginationInfo(resData.pagination);
			} catch (err: any) {
				if (err.name !== 'AbortError') {
					errorHandler(new Error('Failed to fetch dynamic options'), {
						action: 'fetchOptions',
						context: {
							searchQuery: throttledSearchQuery,
							type,
							page: pagination.currentPage,
							pageSize: pagination.pageSize,
						},
						originalError: err,
						statusCode: err instanceof Error && 'status' in err ? err.status : undefined,
					});
				}
			} finally {
				setIsLoading(false);
			}
		},
		[throttledSearchQuery, type, pagination.currentPage, pagination.pageSize],
	);

	useEffect(() => {
		const controller = new AbortController();
		fetchOptions(controller);
		return () => controller.abort();
	}, [fetchOptions]);

	return {
		options,
		isLoading,
		hasMorePages: pagination.currentPage < paginationInfo.totalPages,
		paginationInfo,
	};
};
